import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Grid,
  IconButton,
  Card,
  CardMedia,
  Divider,
  Switch,
  FormControlLabel
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearIcon from '@mui/icons-material/Clear';
import { placeholderImage } from '../utils/imageUtils';

/**
 * Multi-image uploader component with preview functionality
 * Supports both single and multiple file selection
 */
const MultiImageUploader = ({
  onFilesSelect,
  initialFiles = [],
  previewHeight = 150,
  allowCamera = true,
  loading = false,
  maxFiles = 10,
  hideTitle = false,
  hideMultiSelect = false,
  hideInstructions = false,
  buttonText = null
}) => {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [multiSelectMode, setMultiSelectMode] = useState(true); // Set to true by default
  
  // Initialize files when component mounts
  useEffect(() => {
    // If there are initialFiles, make sure we properly log them
    if (initialFiles && initialFiles.length > 0) {
      console.log('Initial files in MultiImageUploader:', initialFiles);
      setFiles(initialFiles);
    }
  }, [initialFiles]);

  // Initialize previews when component mounts or initialFiles change
  useEffect(() => {
    // Process initial files
    if (initialFiles && initialFiles.length > 0) {
      const initialPreviews = initialFiles.map(file => ({
        id: file.id || Math.random().toString(36).substring(2),
        url: file.url || placeholderImage,
        file: file,
        isInitial: true
      }));
      
      // For single image mode, completely replace previews
      if (maxFiles === 1) {
        setPreviews(initialPreviews);
      } else {
        setPreviews(prevPreviews => {
          // Only add initialFiles if they're not already in previews
          const existingIds = prevPreviews.map(p => p.id);
          const newInitialPreviews = initialPreviews.filter(
            p => !existingIds.includes(p.id)
          );
          
          return [...prevPreviews, ...newInitialPreviews];
        });
      }
    }
  }, [initialFiles, maxFiles]);
  
  // Update previews when files change
  useEffect(() => {
    if (files.length === 0) return;
    
    // Generate previews for new files
    const newPreviews = files.map(file => {
      // Check if this is an existing image object (has url property) or a File object
      let url = null;
      let isInitial = false;
      
      if (file && file.url && typeof file.url === 'string') {
        // This is an existing image object from backend
        url = file.url;
        isInitial = true;
      } else if (file && (file instanceof File || file instanceof Blob)) {
        // This is a new File/Blob object from user upload
        try {
          url = URL.createObjectURL(file);
        } catch (error) {
          console.error('Failed to create object URL for file:', file, error);
          url = placeholderImage; // Use placeholder if URL creation fails
        }
      } else {
        console.warn('Invalid file object passed to MultiImageUploader:', file);
        url = placeholderImage; // Use placeholder for invalid files
      }
      
      return {
        id: file.id || Math.random().toString(36).substring(2),
        url: url,
        file: file,
        isInitial: isInitial
      };
    });
    
    // For single image mode, just use the new previews directly
    if (maxFiles === 1) {
      // Clean up old preview URLs to avoid memory leaks, but only URLs not in newPreviews
      const newUrls = new Set(newPreviews.map(p => p.url));
      previews.forEach(preview => {
        if (preview.url && !preview.isInitial && preview.url.startsWith('blob:') && !newUrls.has(preview.url)) {
          try {
            URL.revokeObjectURL(preview.url);
          } catch (err) {
            console.error('Error revoking object URL:', err);
          }
        }
      });
      
      // Just set the new previews, don't merge with existing ones
      setPreviews(newPreviews);
    } else {
      // For multi-select mode, keep the original behavior
      setPreviews(prevPreviews => {
        // Keep only previews of files not in the current files array
        // plus add the new previews for current files
        const filesToKeep = new Set(files.map(f => f.id));
        const keptPreviews = prevPreviews.filter(p => p.isInitial || !p.file || !filesToKeep.has(p.file.id));
        
        return [...keptPreviews, ...newPreviews];
      });
    }
    
    // Don't revoke URLs in the same effect that creates them
    // URLs will be revoked when they're actually replaced or component unmounts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, maxFiles]); // Intentionally excluding 'previews' to avoid infinite re-renders

  // Track blob URLs for cleanup
  const blobUrlsRef = useRef(new Set());

  // Cleanup blob URLs when previews change
  useEffect(() => {
    // Add new blob URLs to tracking
    previews.forEach(preview => {
      if (preview.url && !preview.isInitial && preview.url.startsWith('blob:')) {
        blobUrlsRef.current.add(preview.url);
      }
    });

    // Clean up URLs that are no longer in previews
    const currentUrls = new Set(previews.map(p => p.url));
    for (const url of blobUrlsRef.current) {
      if (!currentUrls.has(url)) {
        try {
          URL.revokeObjectURL(url);
          blobUrlsRef.current.delete(url);
        } catch (err) {
          console.error('Error revoking object URL:', err);
        }
      }
    }

    // Cleanup function for component unmount
    return () => {
      // Capture current URLs to avoid stale closure
      const currentBlobUrls = Array.from(blobUrlsRef.current);
      // Revoke all remaining blob URLs on unmount
      for (const url of currentBlobUrls) {
        try {
          URL.revokeObjectURL(url);
        } catch (err) {
          console.error('Error revoking object URL on unmount:', err);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
      blobUrlsRef.current.clear(); // Safe to access ref in cleanup
    };
  }, [previews]);

  // Handle file input change
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      console.log('Files selected in MultiImageUploader:', e.target.files);
      const allSelectedFiles = Array.from(e.target.files);
      
      // Supported formats that work well with the app
      const supportedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.heic', '.heif', '.jfif'];
      // RAW camera formats that are too large and not supported
      const rawExtensions = ['.cr2', '.cr3', '.nef', '.arw', '.dng', '.raw', '.orf', '.rw2', '.pef', '.srw', '.raf'];
      
      // Filter for valid image types
      const validImageFiles = [];
      const invalidFiles = [];
      const rawFiles = [];
      
      allSelectedFiles.forEach(file => {
        const fileName = file.name.toLowerCase();
        const isRawFormat = rawExtensions.some(ext => fileName.endsWith(ext));
        
        if (isRawFormat) {
          rawFiles.push(file.name);
        } else if (file.type.startsWith('image/') || supportedExtensions.some(ext => fileName.endsWith(ext))) {
          validImageFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });

      // Notify about RAW files specifically
      if (rawFiles.length > 0) {
        alert(`RAW camera files are not supported due to their large size:\n${rawFiles.join(', ')}\n\nPlease use one of these formats instead:\nJPG, PNG, GIF, WebP, HEIC (iPhone), BMP`);
        console.warn('RAW files rejected:', rawFiles);
      }

      if (invalidFiles.length > 0) {
        alert(`The following files are not valid image types and were ignored: ${invalidFiles.join(', ')}\n\nSupported formats: JPG, PNG, GIF, WebP, HEIC, BMP`);
        console.warn('Invalid files skipped:', invalidFiles);
      }

      if (validImageFiles.length === 0) {
        console.log('No valid image files selected.');
        return; // No valid files to process
      }

      // If maxFiles is 1, only use the first valid selected file
      const updatedFiles = maxFiles === 1 ? [validImageFiles[0]] : validImageFiles;

      // Set files in state
      setFiles(updatedFiles);
      console.log('Updated files in MultiImageUploader:', updatedFiles);
      
      // Create new preview objects without immediately creating blob URLs
      // This helps prevent timing issues with URL creation and revocation
      const newPreviews = updatedFiles.map(file => {
        // For images, we can create safe data URLs that don't need to be revoked
        // rather than blob URLs that can cause issues during image processing
        return {
          id: Math.random().toString(36).substring(2),
          // Don't create URL yet - will be done after a delay
          file: file,
          fileName: file.name,  // Store filename for easier debugging
          fileSize: file.size,  // Store file size for reference
          fileType: file.type   // Store file type for reference
        };
      });
      
      // For single image mode, reset completely
      if (maxFiles === 1) {
        // First clean up any existing blob URLs before removing references to them
        previews.forEach(preview => {
          if (preview.url && preview.url.startsWith('blob:') && !preview.isInitial) {
            try {
              URL.revokeObjectURL(preview.url);
              console.log('Revoked old blob URL for:', preview.fileName || 'unknown file');
            } catch (err) {
              console.error('Error revoking old URL:', err);
            }
          }
        });
        
        // Then set the new previews (without URLs yet)
        setPreviews([]);
        
        // Add URLs after a small delay to ensure state is updated
        setTimeout(() => {
          const previewsWithUrls = newPreviews.map(preview => {
            let url = preview.url; // Use the already validated URL from above
            if (!url) {
              if (preview.file && preview.file.url && typeof preview.file.url === 'string') {
                // Existing image object
                url = preview.file.url;
              } else if (preview.file && (preview.file instanceof File || preview.file instanceof Blob)) {
                // New File/Blob object
                try {
                  url = URL.createObjectURL(preview.file);
                } catch (error) {
                  console.error('Failed to create object URL for file:', preview.file, error);
                  url = placeholderImage;
                }
              } else {
                url = placeholderImage;
              }
            }
            return {
              ...preview,
              url: url || placeholderImage
            };
          });
          setPreviews(previewsWithUrls);
        }, 10);
      } else {
        // For multi-select, append to existing (after cleaning any that will be replaced)
        setPreviews(prevState => [
          ...prevState,
          ...newPreviews.map(preview => {
            let url = preview.url; // Use the already validated URL from above
            if (!url) {
              if (preview.file && preview.file.url && typeof preview.file.url === 'string') {
                // Existing image object
                url = preview.file.url;
              } else if (preview.file && (preview.file instanceof File || preview.file instanceof Blob)) {
                // New File/Blob object
                try {
                  url = URL.createObjectURL(preview.file);
                } catch (error) {
                  console.error('Failed to create object URL for file:', preview.file, error);
                  url = placeholderImage;
                }
              } else {
                url = placeholderImage;
              }
            }
            return {
              ...preview,
              url: url || placeholderImage
            };
          })
        ]);
      }
      
      // Notify parent component with the File objects
      if (onFilesSelect) {
        onFilesSelect(updatedFiles);
      }
    }
  };

  // Remove a single file
  const handleRemoveFile = (idToRemove) => {
    const updatedFiles = files.filter(file => file.id !== idToRemove);
    setFiles(updatedFiles);
    
    // Update previews
    setPreviews(previews.filter(preview => preview.id !== idToRemove));
    
    // Notify parent component
    if (onFilesSelect) {
      onFilesSelect(updatedFiles);
    }
  };

  // Clear all selected files
  const handleClearAll = () => {
    setFiles([]);
    
    // Clean up preview URLs
    previews.forEach(preview => {
      if (preview.url && !preview.isInitial) {
        URL.revokeObjectURL(preview.url);
      }
    });
    
    setPreviews([]);
    
    // Notify parent component
    if (onFilesSelect) {
      onFilesSelect([]);
    }
  };

  // Trigger camera input
  const handleTakePhoto = () => {
    const cameraInput = document.getElementById('camera-input');
    if (cameraInput) {
      cameraInput.click();
    }
  };

  // Toggle multi-select mode
  const handleToggleMultiSelect = (event) => {
    setMultiSelectMode(event.target.checked);
  };

  return (
    <Paper sx={{ p: 2, position: 'relative' }}>
      {loading && (
        <Box 
          sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 2
          }}
        >
          <CircularProgress />
        </Box>
      )}
      
      {/* Title and multi-select toggle - conditionally rendered */}
      {!hideTitle && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h3">
            {previews.length > 0 
              ? `Selected Images (${previews.length}/${maxFiles})` 
              : 'Select Images'}
          </Typography>
          
          {!hideMultiSelect && (
            <FormControlLabel
              control={
                <Switch
                  checked={multiSelectMode}
                  onChange={handleToggleMultiSelect}
                  color="primary"
                />
              }
              label="Multi-select"
              labelPlacement="start"
            />
          )}
        </Box>
      )}
      
      {/* Image preview grid */}
      {previews.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={1}>
            {previews.map((preview, index) => (
              // In single image mode, make the image take full width
              <Grid item xs={12} sm={maxFiles === 1 ? 12 : 6} key={preview.id}>
                <Card sx={{ position: 'relative', borderRadius: 1 }}>
                  <CardMedia
                    component="img"
                    image={preview.url}
                    alt="Preview"
                    className="preview-image"
                    onError={(e) => {
                      console.log('Box image failed to load:', preview.url);
                      // Replace with placeholder image on error
                      e.target.src = placeholderImage;
                    }}
                    sx={{ 
                      height: previewHeight,
                      objectFit: 'cover'
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveFile(preview.id)}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      },
                      p: 0.5
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          {/* Clear all button - only show if maxFiles > 1, we have multiple files, AND multiSelectMode is enabled */}
          {maxFiles > 1 && previews.length > 1 && multiSelectMode && (
            <Button
              variant="outlined"
              color="error"
              onClick={handleClearAll}
              startIcon={<DeleteIcon />}
              disabled={loading}
              size="small"
              sx={{ mt: 2 }}
            >
              Clear All
            </Button>
          )}
        </Box>
      )}
      
      {/* File selection buttons */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        {!hideTitle && !hideInstructions && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {multiSelectMode && maxFiles > 1
              ? `Select up to ${maxFiles} images at once`
              : previews.length === 0 ? 'Select an image or take a photo' : 'Replace image'}
          </Typography>
        )}
        
        <Button
          variant="contained"
          component="label"
          startIcon={<FileUploadIcon />}
          disabled={loading}
          sx={{ width: '100%' }}
        >
          {buttonText || (multiSelectMode ? 'Select Files' : 'Choose File')}
          <input
            type="file"
            accept="image/*"
            hidden
            multiple={multiSelectMode}
            onChange={handleFileChange}
          />
        </Button>
        
        {allowCamera && (
          <>
            <Divider sx={{ width: '100%', my: 1 }}>
              <Typography variant="body2" color="text.secondary">
                or
              </Typography>
            </Divider>
            
            <Button
              variant="outlined"
              onClick={handleTakePhoto}
              startIcon={<PhotoCameraIcon />}
              disabled={loading}
              sx={{ width: '100%' }}
            >
              Take Photo
            </Button>
            
            {/* Hidden camera input */}
            <input
              id="camera-input"
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={handleFileChange}
            />
          </>
        )}
      </Box>
    </Paper>
  );
};

export default MultiImageUploader;
